import React, { useState, useEffect, useCallback } from 'react'
import { supabase } from '../supabaseClient'
import { USER_ROLES } from '../config/security'
import {
  gerarPDFProposta,
  gerarPropostaPPTX as gerarPropostaPPTXDocumento,
  isLxp,
  montarDadosDocumentoCalculadora,
  usaFluxoPptx
} from '../utils/propostaDocumento'
import { calcularSistemaPadrao } from '../utils/calculoSistemaSolar'
import './CalculadoraSolar.css'

// Documento da proposta (PDF/PPTX) é gerado em src/utils/propostaDocumento.
// Consulta de Propostas usa o mesmo módulo — altere lá, não aqui.

const CalculadoraSolar = ({ userRole, userId, userEmpresa }) => {
  const [kwhOptions, setKwhOptions] = useState([])
  const [modulosOptions, setModulosOptions] = useState([])
  const [loading, setLoading] = useState(false)
  const [calculando, setCalculando] = useState(false)
  const [salvandoProposta, setSalvandoProposta] = useState(false)
  const [gerandoPropostaPPTX, setGerandoPropostaPPTX] = useState(false)
  
  // Estados do modal de confirmação
  const [showModal, setShowModal] = useState(false)
  const [modalData, setModalData] = useState({
    type: 'success', // success, error, warning, info
    title: '',
    message: '',
    onConfirm: null
  })
  
  // Estados do formulário
  const [formData, setFormData] = useState({
    nomeCliente: '',
    cpf: '',
    kwh: '',
    modulo: '',
    majoracao: 0,
    kwhSelecionado: null,
    moduloSelecionado: null,
    tipoProposta: 'padrao',
    kitPersonalizadoId: '',
    kitPersonalizadoSelecionado: null,
    kitValorSelecionado: null
  })
  
  // Estados dos resultados
  const [resultados, setResultados] = useState({
    projetoKwp: 0,
    quantidadePaineis: 0,
    inversor: 0,
    valorKit: 0,
    totalComPorcentagem: 0
  })
  
  const [showResults, setShowResults] = useState(false)

  // Estados para modal de novo módulo (apenas administrador)
  const [valorKitPersonalizadoOptions, setValorKitPersonalizadoOptions] = useState([])
  const [showNovoModuloModal, setShowNovoModuloModal] = useState(false)
  const [novoModuloForm, setNovoModuloForm] = useState({
    modulo: '',
    potencia_wp: ''
  })
  const [salvandoModulo, setSalvandoModulo] = useState(false)

  // Carregar opções de Kwh
  const loadKwhOptions = useCallback(async () => {
    try {
      let query = supabase
        .from('valor_kit')
        .select('id, kwh, valor_kit, porcentagem, total')
        .order('kwh', { ascending: true })

      if (userEmpresa?.id) {
        query = query.eq('empresa_id', userEmpresa.id)
      }

      const { data, error } = await query

      if (error) throw error
      
      setKwhOptions(data || [])
    } catch (error) {
      console.error('Erro ao carregar opções de Kwh:', error)
    }
  }, [userEmpresa])

  // Carregar opções de módulos
  const loadModulosOptions = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('modulos_proposta')
        .select('id, modulo, potencia_wp')
        .order('potencia_wp', { ascending: true })

      if (error) throw error
      
      setModulosOptions(data || [])
    } catch (error) {
      console.error('Erro ao carregar opções de módulos:', error)
    }
  }, [])

  // Carregar kits personalizados
  const loadValorKitPersonalizado = useCallback(async () => {
    try {
      let query = supabase
        .from('valor_kit_personalizado')
        .select('id, kwh, valor_kit, porcentagem, total, qtd_inversor, qtd_modulos')
        .order('kwh', { ascending: true })
      if (userEmpresa?.id) {
        query = query.or(`empresa_id.eq.${userEmpresa.id},empresa_id.is.null`)
      }
      const { data, error } = await query
      if (error) throw error
      setValorKitPersonalizadoOptions(data || [])
    } catch (error) {
      console.error('Erro ao carregar kits personalizados:', error)
      setValorKitPersonalizadoOptions([])
    }
  }, [userEmpresa?.id])

  // Função para salvar novo módulo
  const handleSalvarNovoModulo = async (e) => {
    e.preventDefault()
    
    if (!novoModuloForm.modulo.trim() || !novoModuloForm.potencia_wp) {
      showModalConfirmation(
        'warning',
        '⚠️ Campos Obrigatórios',
        'Por favor, preencha todos os campos.',
        null
      )
      return
    }

    const potencia = parseInt(novoModuloForm.potencia_wp)
    if (isNaN(potencia) || potencia <= 0) {
      showModalConfirmation(
        'warning',
        '⚠️ Potência Inválida',
        'A potência deve ser um número positivo.',
        null
      )
      return
    }

    setSalvandoModulo(true)
    try {
      const { data, error } = await supabase
        .from('modulos_proposta')
        .insert([
          {
            modulo: novoModuloForm.modulo.trim(),
            potencia_wp: potencia
          }
        ])
        .select()
        .single()

      if (error) throw error

      showModalConfirmation(
        'success',
        '✅ Módulo Adicionado',
        `O módulo "${novoModuloForm.modulo.trim()}" (${potencia}W) foi adicionado com sucesso!`,
        () => {
          setShowNovoModuloModal(false)
          setNovoModuloForm({ modulo: '', potencia_wp: '' })
          loadModulosOptions()
        }
      )
    } catch (error) {
      console.error('Erro ao salvar módulo:', error)
      showModalConfirmation(
        'error',
        '❌ Erro ao Salvar',
        `Erro ao adicionar módulo: ${error.message}`,
        null
      )
    } finally {
      setSalvandoModulo(false)
    }
  }

  // Carregar dados iniciais
  useEffect(() => {
    const loadData = async () => {
      setLoading(true)
      try {
        await Promise.all([
          loadKwhOptions(),
          loadModulosOptions(),
          loadValorKitPersonalizado()
        ])
      } catch (error) {
        console.error('Erro ao carregar dados:', error)
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [loadKwhOptions, loadModulosOptions, loadValorKitPersonalizado])

  // Função para calcular o sistema solar
  const calcularSistemaSolar = () => {
    const isPadrao = formData.tipoProposta === 'padrao'
    if (isPadrao) {
      if (!formData.kwh || !formData.modulo) {
        showModalConfirmation(
          'warning',
          '⚠️ Campos Obrigatórios',
          'Por favor, selecione o Consumo Mensal e o Módulo para calcular.',
          null
        )
        return
      }
    } else {
      if (!formData.kitPersonalizadoId) {
        showModalConfirmation(
          'warning',
          '⚠️ Campos Obrigatórios',
          'Por favor, selecione o kit personalizado para calcular.',
          null
        )
        return
      }
    }

    const msg = isPadrao
      ? `Deseja calcular o sistema solar com os seguintes parâmetros?\n\n• Tipo: Padrão\n• Consumo Mensal: ${formData.kwh} kWh\n• Módulo: ${moduloParaProposta?.modulo || 'Não selecionado'}\n• Potência: ${moduloParaProposta?.potencia_wp || 0}W`
      : `Deseja calcular o sistema solar com os seguintes parâmetros?\n\n• Tipo: Personalizada\n• Kit: ${formData.kitPersonalizadoSelecionado?.kwh || ''} kWh`
    showModalConfirmation('info', '🧮 Confirmar Cálculo', msg, () => executarCalculoSistema())
  }

  // Função separada para executar o cálculo
  const executarCalculoSistema = async () => {
    setCalculando(true)
    try {
      const isPadrao = formData.tipoProposta === 'padrao'

      if (isPadrao) {
        const kwh = parseInt(formData.kwh)
        const potenciaModulo = formData.moduloSelecionado?.potencia_wp || 0
        const kitSelecionado = kwhOptions.find(kit => Number(kit.kwh) === kwh)
        setResultados(calcularSistemaPadrao({
          kwh,
          potenciaModuloWp: potenciaModulo,
          kit: kitSelecionado
        }))
      } else {
        const kit = formData.kitPersonalizadoSelecionado
        if (!kit) throw new Error('Kit personalizado não selecionado')

        const { data: kitValores } = await supabase
          .from('kits_valores_personalizado')
          .select('marca_inversor, potencia_inversor, marca_modulo, potencia_modulo')
          .eq('id_kit_personalizado', kit.id)
          .limit(1)

        const kv = kitValores?.[0]
        const potenciaModulo = Number(kv?.potencia_modulo) || 0
        const potenciaInversor = Number(kv?.potencia_inversor) || 0
        const qtdModulos = Number(kit.qtd_modulos) || 0
        const qtdInversor = Number(kit.qtd_inversor) || 1

        const projetoKwp = potenciaModulo > 0 ? (potenciaModulo * qtdModulos) / 1000 : 0
        const inversor = potenciaInversor * qtdInversor

        setResultados({
          projetoKwp: parseFloat(projetoKwp.toFixed(2)),
          quantidadePaineis: qtdModulos,
          inversor: parseFloat(inversor.toFixed(2)),
          valorKit: Number(kit.valor_kit) || 0,
          totalComPorcentagem: Number(kit.total) || 0
        })
        setFormData(prev => ({ ...prev, kitValorSelecionado: kv }))
      }

      setShowResults(true)
      showModalConfirmation(
        'success',
        '✅ Cálculo Concluído!',
        'Sistema solar calculado com sucesso!\n\nOs resultados estão disponíveis abaixo.',
        null
      )
    } catch (error) {
      console.error('Erro ao calcular sistema solar:', error)
      showModalConfirmation(
        'error',
        '❌ Erro no Cálculo',
        'Erro ao calcular o sistema solar. Tente novamente.',
        null
      )
    } finally {
      setCalculando(false)
    }
  }

  const dadosDocumentoAtual = () => {
    const modulo = formData.tipoProposta === 'personalizada' && formData.kitValorSelecionado
      ? { modulo: formData.kitValorSelecionado.marca_modulo || '–', potencia_wp: formData.kitValorSelecionado.potencia_modulo || 0 }
      : formData.moduloSelecionado
    const kwh = formData.tipoProposta === 'personalizada'
      ? formData.kitPersonalizadoSelecionado?.kwh
      : formData.kwh
    return montarDadosDocumentoCalculadora({
      formData,
      resultados,
      kwhParaProposta: kwh,
      moduloParaProposta: modulo
    })
  }

  // Função para gerar proposta a partir do template PPTX (Clarear/LXP)
  // cadastrada: quando true, a proposta já foi cadastrada pelo caller; não exibe modal de sucesso (caller exibe)
  const gerarPropostaPPTX = async (cadastrada = false) => {
    if (!showResults) {
      showModalConfirmation(
        'warning',
        '⚠️ Cálculo Necessário',
        'Por favor, calcule o sistema solar antes de gerar a proposta.',
        null
      )
      return
    }
    if (!formData.nomeCliente?.trim()) {
      showModalConfirmation(
        'warning',
        '⚠️ Nome Obrigatório',
        'Por favor, informe o nome do cliente.',
        null
      )
      return
    }

    setGerandoPropostaPPTX(true)
    try {
      const result = await gerarPropostaPPTXDocumento({
        dados: dadosDocumentoAtual(),
        userEmpresa
      })
      if (!cadastrada) {
        showModalConfirmation(
          'success',
          '✅ Proposta gerada!',
          `Proposta em PowerPoint gerada com sucesso a partir do modelo ${result.templateLabel} (não cadastrada no sistema).`,
          null
        )
      }
    } catch (error) {
      console.error('Erro ao gerar proposta PPTX:', error)
      showModalConfirmation(
        'error',
        '❌ Erro ao Gerar Proposta',
        `Não foi possível gerar a proposta: ${error.message}. Verifique se o template correspondente da empresa está disponível em /docs/.`,
        null
      )
    } finally {
      setGerandoPropostaPPTX(false)
    }
  }

  // Função para gerar PDF da proposta (módulo compartilhado com Consulta de Propostas)
  const gerarPDF = async () => {
    if (!showResults) {
      showModalConfirmation(
        'warning',
        '⚠️ Cálculo Necessário',
        'Por favor, calcule o sistema solar antes de gerar o PDF.',
        null
      )
      return
    }

    try {
      const result = await gerarPDFProposta({
        dados: dadosDocumentoAtual(),
        userEmpresa
      })
      showModalConfirmation(
        'success',
        '✅ Proposta gerada!',
        `Proposta em PDF e HTML gerada com sucesso!\n\nPDF: ${result.fileName}${result.htmlFileName ? `\nHTML: ${result.htmlFileName}` : ''}`,
        null
      )
    } catch (error) {
      console.error('Erro ao gerar PDF:', error)
      showModalConfirmation(
        'error',
        '❌ Erro ao Gerar PDF',
        'Erro ao gerar o PDF da proposta. Tente novamente.',
        null
      )
    }
  }

  // Função para salvar proposta e gerar PDF em uma única ação
  const salvarPropostaEGerarPDF = async () => {
    // Validações
    if (!formData.nomeCliente.trim()) {
      showModalConfirmation(
        'warning',
        '⚠️ Nome Obrigatório',
        'Por favor, informe o nome do cliente antes de salvar a proposta.',
        null
      )
      return
    }

    if (!showResults) {
      showModalConfirmation(
        'warning',
        '⚠️ Cálculo Necessário',
        'Por favor, calcule o sistema solar antes de salvar a proposta.',
        null
      )
      return
    }

    setSalvandoProposta(true)

    try {
      // Primeiro, vamos testar se a tabela existe e está acessível
      console.log('Testando acesso à tabela propostas_clientes...')
      const { data: testData, error: testError } = await supabase
        .from('propostas_clientes')
        .select('id')
        .limit(1)

      if (testError) {
        console.error('Erro ao acessar tabela:', testError)
        showModalConfirmation(
          'error',
          '❌ Erro de Conexão',
          `Erro ao acessar a tabela de propostas: ${testError.message}`,
          null
        )
        return
      }

      console.log('Tabela acessível, prosseguindo com inserção...')

      // Preparar dados da proposta
      const propostaData = {
        nome_cliente: formData.nomeCliente.trim(),
        cpf: formData.cpf || null,
        potencia_inversor: resultados.inversor,
        modulo_potencia: moduloParaProposta?.potencia_wp || 0,
        quantidade_placas: resultados.quantidadePaineis,
        kwp_proposta: resultados.projetoKwp,
        valor_kit: resultados.valorKit,
        porcentagem: porcentagemParaProposta || 0,
        majorado: parseFloat(formData.majoracao) || 0,
        total_proposta: resultados.totalComPorcentagem + (parseFloat(formData.majoracao) || 0),
        data_proposta: new Date().toISOString(),
        data_validade: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString(),
        id_usuario_responsavel: userId,
        status: 1 // Status 1 = Ativo
      }

      console.log('Dados da proposta:', propostaData)

      // Salvar no banco de dados
      const { data, error } = await supabase
        .from('propostas_clientes')
        .insert([propostaData])
        .select()

      if (error) {
        console.error('Erro ao salvar proposta:', error)
        showModalConfirmation(
          'error',
          '❌ Erro ao Salvar',
          `Erro ao salvar a proposta: ${error.message}`,
          null
        )
        return
      }

      console.log('Proposta salva com sucesso:', data)

      // Se chegou até aqui, a proposta foi salva com sucesso
      // Agora gerar o PDF
      gerarPDF()

      // Mostrar confirmação de sucesso
      showModalConfirmation(
        'success',
        '✅ Proposta Salva e PDF Gerado!',
        `Proposta salva com sucesso no banco de dados!\n\nID da Proposta: ${data[0]?.id || 'N/A'}\n\nO PDF foi gerado automaticamente.`,
        null
      )

    } catch (error) {
      console.error('Erro ao salvar proposta:', error)
      showModalConfirmation(
        'error',
        '❌ Erro ao Salvar',
        `Erro inesperado ao salvar a proposta: ${error.message}`,
        null
      )
    } finally {
      setSalvandoProposta(false)
    }
  }

  // Botão GERAR unificado: Clarear/LXP = salvar + PPTX (modelo específico); demais = salvar + PDF
  const gerarProposta = async () => {
    if (!formData.nomeCliente?.trim()) {
      showModalConfirmation(
        'warning',
        '⚠️ Nome Obrigatório',
        'Por favor, informe o nome do cliente antes de gerar a proposta.',
        null
      )
      return
    }
    if (!showResults) {
      showModalConfirmation(
        'warning',
        '⚠️ Cálculo Necessário',
        'Por favor, calcule o sistema solar antes de gerar a proposta.',
        null
      )
      return
    }

    const fluxoPptx = usaFluxoPptx(userEmpresa?.id)

    setSalvandoProposta(true)
    try {
      if (fluxoPptx) {
        // Clarear/LXP: cadastrar proposta e gerar PPTX (modelo específico por empresa)
        const { data: testData, error: testError } = await supabase
          .from('propostas_clientes')
          .select('id')
          .limit(1)
        if (testError) {
          showModalConfirmation(
            'error',
            '❌ Erro de Conexão',
            `Erro ao acessar a tabela de propostas: ${testError.message}`,
            null
          )
          return
        }
        const propostaData = {
          nome_cliente: formData.nomeCliente.trim(),
          cpf: formData.cpf || null,
          potencia_inversor: resultados.inversor,
          modulo_potencia: moduloParaProposta?.potencia_wp || 0,
          quantidade_placas: resultados.quantidadePaineis,
          kwp_proposta: resultados.projetoKwp,
          valor_kit: resultados.valorKit,
          porcentagem: porcentagemParaProposta || 0,
          majorado: parseFloat(formData.majoracao) || 0,
          total_proposta: resultados.totalComPorcentagem + (parseFloat(formData.majoracao) || 0),
          data_proposta: new Date().toISOString(),
          data_validade: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString(),
          id_usuario_responsavel: userId,
          status: 1
        }
        const { data, error } = await supabase
          .from('propostas_clientes')
          .insert([propostaData])
          .select()
        if (error) {
          showModalConfirmation(
            'error',
            '❌ Erro ao Salvar',
            `Erro ao salvar a proposta: ${error.message}`,
            null
          )
          return
        }
        await gerarPropostaPPTX(true)
        const nomeModelo = isLxp(userEmpresa?.id) ? 'LXP' : 'Clarear'
        showModalConfirmation(
          'success',
          '✅ Proposta Salva e Gerada!',
          `Proposta cadastrada no sistema e proposta em PowerPoint gerada (modelo ${nomeModelo}).\n\nID da Proposta: ${data?.[0]?.id || 'N/A'}`,
          null
        )
      } else {
        await salvarPropostaEGerarPDF()
      }
    } catch (error) {
      console.error('Erro ao gerar proposta:', error)
      showModalConfirmation(
        'error',
        '❌ Erro ao Gerar',
        `Erro inesperado: ${error.message}`,
        null
      )
    } finally {
      setSalvandoProposta(false)
    }
  }

  // Função para salvar proposta no banco de dados (mantida para compatibilidade)
  const salvarProposta = async () => {
    // Validações
    if (!formData.nomeCliente.trim()) {
      showModalConfirmation(
        'warning',
        '⚠️ Nome Obrigatório',
        'Por favor, informe o nome do cliente.',
        null
      )
      return
    }

    if (formData.cpf && !isValidCPF(formData.cpf)) {
      showModalConfirmation(
        'error',
        '❌ CPF Inválido',
        'CPF inválido. Por favor, verifique o número digitado.',
        null
      )
      return
    }

    if (!showResults) {
      showModalConfirmation(
        'warning',
        '⚠️ Cálculo Necessário',
        'Por favor, calcule o sistema solar antes de salvar a proposta.',
        null
      )
      return
    }

    setSalvandoProposta(true)
    
    try {
      const propostaData = {
        nome_cliente: formData.nomeCliente.trim(),
        cpf: formData.cpf || null,
        potencia_inversor: resultados.inversor,
        modulo_potencia: moduloParaProposta?.potencia_wp || 0,
        quantidade_placas: resultados.quantidadePaineis,
        kwp_proposta: resultados.projetoKwp,
        valor_kit: resultados.valorKit,
        porcentagem: porcentagemParaProposta || 0,
        total_proposta: resultados.totalComPorcentagem,
        data_proposta: new Date().toISOString(),
        data_validade: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString(),
        id_usuario_responsavel: userId,
        status: 1 // Status 1 = Ativo
      }

      const { data, error } = await supabase
        .from('propostas_clientes')
        .insert([propostaData])
        .select()

      if (error) throw error
      
      showModalConfirmation(
        'success',
        '✅ Proposta Salva!',
        `Proposta salva com sucesso!\n\nID da Proposta: ${data[0].id}\nValidade: ${new Date(data[0].data_validade).toLocaleDateString('pt-BR')}`,
        () => limparFormulario()
      )
      
    } catch (error) {
      console.error('Erro ao salvar proposta:', error)
      showModalConfirmation(
        'error',
        '❌ Erro ao Salvar',
        'Erro ao salvar a proposta: ' + error.message,
        null
      )
    } finally {
      setSalvandoProposta(false)
    }
  }

  // Função para limpar formulário
  const limparFormulario = () => {
    setFormData({
      nomeCliente: '',
      cpf: '',
      kwh: '',
      modulo: '',
      majoracao: 0,
      kwhSelecionado: null,
      moduloSelecionado: null,
      tipoProposta: 'padrao',
      kitPersonalizadoId: '',
      kitPersonalizadoSelecionado: null,
      kitValorSelecionado: null
    })
    setResultados({
      projetoKwp: 0,
      quantidadePaineis: 0,
      inversor: 0,
      valorKit: 0,
      totalComPorcentagem: 0
    })
    setShowResults(false)
  }

  // Função para formatar moeda
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value)
  }

  // Função para formatar CPF
  const formatCPF = (value) => {
    const cpf = value.replace(/\D/g, '')
    return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
  }

  // Função para validar CPF (opcional)
  const isValidCPF = (cpf) => {
    if (!cpf) return true // CPF é opcional
    const cleanCPF = cpf.replace(/\D/g, '')
    if (cleanCPF.length !== 11) return false
    
    // Validação básica de CPF
    if (/^(\d)\1{10}$/.test(cleanCPF)) return false
    
    let sum = 0
    for (let i = 0; i < 9; i++) {
      sum += parseInt(cleanCPF.charAt(i)) * (10 - i)
    }
    let remainder = (sum * 10) % 11
    if (remainder === 10 || remainder === 11) remainder = 0
    if (remainder !== parseInt(cleanCPF.charAt(9))) return false
    
    sum = 0
    for (let i = 0; i < 10; i++) {
      sum += parseInt(cleanCPF.charAt(i)) * (11 - i)
    }
    remainder = (sum * 10) % 11
    if (remainder === 10 || remainder === 11) remainder = 0
    if (remainder !== parseInt(cleanCPF.charAt(10))) return false
    
    return true
  }

  // Função para mostrar modal de confirmação
  const showModalConfirmation = (type, title, message, onConfirm = null) => {
    setModalData({
      type,
      title,
      message,
      onConfirm
    })
    setShowModal(true)
  }

  // Função para fechar modal
  const closeModal = () => {
    setShowModal(false)
    setModalData({
      type: 'success',
      title: '',
      message: '',
      onConfirm: null
    })
  }

  // Função para confirmar ação do modal
  const handleModalConfirm = () => {
    if (modalData.onConfirm) {
      modalData.onConfirm()
    }
    closeModal()
  }

  // Handler para mudança de Kwh
  const handleKwhChange = (e) => {
    const kwhValue = e.target.value
    const kwhSelecionado = kwhOptions.find(kit => kit.kwh === parseInt(kwhValue))
    
    setFormData(prev => ({
      ...prev,
      kwh: kwhValue,
      kwhSelecionado: kwhSelecionado
    }))
  }

  // Handler para mudança de módulo
  const handleModuloChange = (e) => {
    const moduloId = e.target.value
    const moduloSelecionado = modulosOptions.find(modulo => modulo.id === parseInt(moduloId))
    
    setFormData(prev => ({
      ...prev,
      modulo: moduloId,
      moduloSelecionado: moduloSelecionado
    }))
  }

  // Handler para tipo de proposta
  const handleTipoPropostaChange = (e) => {
    const tipo = e.target.value
    setFormData(prev => ({
      ...prev,
      tipoProposta: tipo,
      kitPersonalizadoId: '',
      kitPersonalizadoSelecionado: null,
      kitValorSelecionado: null,
      ...(tipo === 'padrao' ? {} : { kwh: '', modulo: '', kwhSelecionado: null, moduloSelecionado: null })
    }))
    setShowResults(false)
  }

  // Handler para kit personalizado
  const handleKitPersonalizadoChange = (e) => {
    const id = e.target.value
    const kit = valorKitPersonalizadoOptions.find(k => k.id === parseInt(id))
    setFormData(prev => ({
      ...prev,
      kitPersonalizadoId: id,
      kitPersonalizadoSelecionado: kit || null
    }))
  }

  // Módulo para proposta (padrão ou personalizado)
  const moduloParaProposta = formData.tipoProposta === 'personalizada' && formData.kitValorSelecionado
    ? { modulo: formData.kitValorSelecionado.marca_modulo || '–', potencia_wp: formData.kitValorSelecionado.potencia_modulo || 0 }
    : formData.moduloSelecionado

  // Porcentagem para proposta
  const porcentagemParaProposta = formData.tipoProposta === 'personalizada'
    ? formData.kitPersonalizadoSelecionado?.porcentagem
    : formData.kwhSelecionado?.porcentagem

  // Kwh para proposta (padrão ou personalizado)
  const kwhParaProposta = formData.tipoProposta === 'personalizada'
    ? formData.kitPersonalizadoSelecionado?.kwh
    : formData.kwh

  // Handler para mudança de nome do cliente
  const handleNomeClienteChange = (e) => {
    setFormData(prev => ({
      ...prev,
      nomeCliente: e.target.value
    }))
  }

  // Handler para mudança de CPF
  const handleCPFChange = (e) => {
    const formattedCPF = formatCPF(e.target.value)
    setFormData(prev => ({
      ...prev,
      cpf: formattedCPF
    }))
  }

  // Handler para mudança de Majoração
  const handleMajoracaoChange = (e) => {
    const value = e.target.value
    // Permitir apenas números e ponto decimal
    const numericValue = value.replace(/[^0-9.]/g, '')
    
    setFormData(prev => ({
      ...prev,
      majoracao: numericValue
    }))
  }

  if (loading) {
    return (
      <div className="calculadora-container">
        <div className="loading">
          <div className="spinner"></div>
          <p>Carregando dados da calculadora...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="calculadora-container">
      <div className="calculadora-header">
        <h1>☀️ Calculadora Solar</h1>
        <p>Calcule o sistema solar ideal para seu cliente</p>
      </div>

      <div className="calculadora-content">
        <div className="calculadora-form">
          <h2>📊 Dados do Projeto</h2>
          
          {/* Dados do Cliente */}
          <div className="cliente-section">
            <h3>Dados do Cliente</h3>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="nome-cliente">Nome do Cliente: <span className="required">*</span></label>
                <input
                  type="text"
                  id="nome-cliente"
                  value={formData.nomeCliente}
                  onChange={handleNomeClienteChange}
                  className="form-input"
                  placeholder="Digite o nome completo do cliente"
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="cpf-cliente">CPF: <span className="optional">(opcional)</span></label>
                <input
                  type="text"
                  id="cpf-cliente"
                  value={formData.cpf}
                  onChange={handleCPFChange}
                  className="form-input"
                  placeholder="000.000.000-00"
                  maxLength="14"
                />
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="majoracao">Majoração: <span className="optional">(opcional)</span></label>
                <input
                  type="text"
                  id="majoracao"
                  value={formData.majoracao}
                  onChange={handleMajoracaoChange}
                  className="form-input"
                  placeholder="0,00"
                />
              </div>
            </div>
          </div>

          {/* Dados Técnicos */}
          <div className="tecnico-section">
            <h3>⚡ Dados Técnicos</h3>

            <div className="form-group">
              <label htmlFor="tipo-proposta">Tipo de Proposta:</label>
              <select
                id="tipo-proposta"
                value={formData.tipoProposta}
                onChange={handleTipoPropostaChange}
                className="elegant-select"
              >
                <option value="padrao">Padrão</option>
                <option value="personalizada">Personalizada</option>
              </select>
            </div>

            {formData.tipoProposta === 'padrao' ? (
              <>
                <div className="form-group">
                  <label htmlFor="kwh-select">Consumo Mensal (Kwh):</label>
                  <select
                    id="kwh-select"
                    value={formData.kwh}
                    onChange={handleKwhChange}
                    className="elegant-select"
                  >
                    <option value="">Selecione o consumo mensal</option>
                    {kwhOptions.map((kit) => (
                      <option key={kit.id} value={kit.kwh}>
                        {kit.kwh} Kwh
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: '200px' }}>
                      <label htmlFor="modulo-select">Módulo Solar:</label>
                      <select
                        id="modulo-select"
                        value={formData.modulo}
                        onChange={handleModuloChange}
                        className="elegant-select"
                        style={{ width: '100%' }}
                      >
                        <option value="">Selecione o módulo</option>
                        {modulosOptions.map((modulo) => (
                          <option key={modulo.id} value={modulo.id}>
                            {modulo.modulo} ({modulo.potencia_wp}W)
                          </option>
                        ))}
                      </select>
                    </div>
                    {userRole === USER_ROLES.ADMINISTRADOR && (
                      <button
                        type="button"
                        onClick={() => setShowNovoModuloModal(true)}
                        style={{
                          marginTop: '24px',
                          padding: '10px 20px',
                          backgroundColor: '#28a745',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: '500',
                          transition: 'background-color 0.3s',
                          whiteSpace: 'nowrap'
                        }}
                        onMouseOver={(e) => e.target.style.backgroundColor = '#218838'}
                        onMouseOut={(e) => e.target.style.backgroundColor = '#28a745'}
                      >
                        ➕ Novo Módulo
                      </button>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div className="form-group">
                <label htmlFor="kit-personalizado-select">Kit Personalizado:</label>
                <select
                  id="kit-personalizado-select"
                  value={formData.kitPersonalizadoId}
                  onChange={handleKitPersonalizadoChange}
                  className="elegant-select"
                >
                  <option value="">Selecione o kit personalizado</option>
                  {valorKitPersonalizadoOptions.map((kit) => (
                    <option key={kit.id} value={kit.id}>
                      {kit.kwh} Kwh – {formatCurrency(kit.total || 0)}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="form-actions">
            <button
              type="button"
              onClick={calcularSistemaSolar}
              disabled={calculando || !formData.nomeCliente.trim() || (formData.tipoProposta === 'padrao' ? (!formData.kwh || !formData.modulo) : !formData.kitPersonalizadoId)}
              className="btn btn-primary"
            >
              {calculando ? '⏳ Calculando...' : '🧮 Calcular Sistema'}
            </button>
            
            <button
              type="button"
              onClick={limparFormulario}
              className="btn btn-secondary"
            >
              🗑️ Limpar
            </button>
          </div>
        </div>

        {showResults && (
          <div className="calculadora-results">
            <h2>📈 Resultados do Cálculo</h2>
            
            <div className="results-grid">
              <div className="result-card">
                <div className="result-icon">⚡</div>
                <div className="result-content">
                  <h3>Projeto em Kwp</h3>
                  <p className="result-value">{resultados.projetoKwp} Kwp</p>
                </div>
              </div>

              <div className="result-card">
                <div className="result-icon">🔋</div>
                <div className="result-content">
                  <h3>Quantidade de Painéis</h3>
                  <p className="result-value">{resultados.quantidadePaineis} painéis</p>
                </div>
              </div>

              <div className="result-card">
                <div className="result-icon">🔌</div>
                <div className="result-content">
                  <h3>Inversor</h3>
                  <p className="result-value">{resultados.inversor} Kw</p>
                </div>
              </div>

              <div className="result-card">
                <div className="result-icon">💰</div>
                <div className="result-content">
                  <h3>Majoração</h3>
                  <p className="result-value">{formatCurrency(formData.majoracao || 0)}</p>
                </div>
              </div>

              <div className="result-card total-card">
                <div className="result-icon">💎</div>
                <div className="result-content">
                  <h3>Total Final</h3>
                  <p className="result-value total-value">{formatCurrency(resultados.totalComPorcentagem + (parseFloat(formData.majoracao) || 0))}</p>
                </div>
              </div>
            </div>

            <div className="calculation-summary">
              <h3>📋 Resumo do Projeto</h3>
              <div className="summary-details">
                <p><strong>Cliente:</strong> {formData.nomeCliente}</p>
                {formData.cpf && <p><strong>CPF:</strong> {formData.cpf}</p>}
                <p><strong>Consumo Mensal:</strong> {formData.tipoProposta === 'personalizada' ? formData.kitPersonalizadoSelecionado?.kwh : formData.kwh} Kwh</p>
                <p><strong>Módulo Selecionado:</strong> {moduloParaProposta?.modulo}</p>
                <p><strong>Potência do Módulo:</strong> {moduloParaProposta?.potencia_wp}W</p>
                <p><strong>Projeto Calculado:</strong> {resultados.projetoKwp} Kwp</p>
                <p><strong>Quantidade de Painéis:</strong> {resultados.quantidadePaineis} unidades</p>
                <p><strong>Inversor Recomendado:</strong> {resultados.inversor} Kw</p>
                <p><strong>Majoração:</strong> {formatCurrency(formData.majoracao || 0)}</p>
                <p><strong>Total Final:</strong> {formatCurrency(resultados.totalComPorcentagem + (parseFloat(formData.majoracao) || 0))}</p>
              </div>
            </div>

            {/* Botão GERAR unificado: Clarear = salvar + PPTX (modelo Clarear); demais = salvar + PDF */}
            <div className="proposta-actions">
              <div className="proposta-buttons">
                <button
                  onClick={gerarProposta}
                  disabled={salvandoProposta || gerandoPropostaPPTX}
                  className="btn btn-primary btn-lg btn-unified"
                  style={{
                    background: 'linear-gradient(135deg, #28a745 0%, #007bff 100%)',
                    border: 'none',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '1.1rem',
                    padding: '12px 30px',
                    borderRadius: '8px',
                    boxShadow: '0 4px 15px rgba(0, 123, 255, 0.3)',
                    transition: 'all 0.3s ease',
                    minWidth: '220px'
                  }}
                >
                  {salvandoProposta || gerandoPropostaPPTX ? '⏳ Gerando...' : '📑 GERAR'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal de Confirmação */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className={`modal-header modal-${modalData.type}`}>
              <h2>{modalData.title}</h2>
              <button onClick={closeModal} className="close-button">
                ✕
              </button>
            </div>
            
            <div className="modal-body">
              <div className="modal-icon">
                {modalData.type === 'success' && '✅'}
                {modalData.type === 'error' && '❌'}
                {modalData.type === 'warning' && '⚠️'}
                {modalData.type === 'info' && 'ℹ️'}
              </div>
              <p className="modal-message">{modalData.message}</p>
            </div>
            
            <div className="modal-footer">
              <button
                onClick={closeModal}
                className="btn btn-secondary"
              >
                Fechar
              </button>
              {modalData.onConfirm && (
                <button
                  onClick={handleModalConfirm}
                  className={`btn btn-${modalData.type === 'success' ? 'success' : 'primary'}`}
                >
                  Confirmar
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal para adicionar novo módulo (apenas administrador) */}
      {showNovoModuloModal && (
        <div 
          className="modal-overlay-calculadora"
          onClick={() => !salvandoModulo && setShowNovoModuloModal(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}
        >
          <div 
            className="modal-content-calculadora"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '500px',
              width: '100%',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)'
            }}
          >
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '20px',
              paddingBottom: '15px',
              borderBottom: '2px solid #e0e0e0'
            }}>
              <h2 style={{ margin: 0, color: '#333', fontSize: '1.5rem' }}>➕ Adicionar Novo Módulo</h2>
              <button
                onClick={() => !salvandoModulo && setShowNovoModuloModal(false)}
                disabled={salvandoModulo}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '28px',
                  cursor: salvandoModulo ? 'not-allowed' : 'pointer',
                  color: '#999',
                  padding: '0',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '4px',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => !salvandoModulo && (e.target.style.backgroundColor = '#f0f0f0')}
                onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSalvarNovoModulo}>
              <div style={{ marginBottom: '20px' }}>
                <label 
                  htmlFor="novo-modulo-nome"
                  style={{
                    display: 'block',
                    marginBottom: '8px',
                    color: '#555',
                    fontWeight: '500',
                    fontSize: '14px'
                  }}
                >
                  Nome do Módulo: <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  type="text"
                  id="novo-modulo-nome"
                  value={novoModuloForm.modulo}
                  onChange={(e) => setNovoModuloForm({ ...novoModuloForm, modulo: e.target.value })}
                  placeholder="Ex: Módulo 550W"
                  required
                  disabled={salvandoModulo}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    transition: 'border-color 0.3s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#667eea'}
                  onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                />
              </div>

              <div style={{ marginBottom: '25px' }}>
                <label 
                  htmlFor="novo-modulo-potencia"
                  style={{
                    display: 'block',
                    marginBottom: '8px',
                    color: '#555',
                    fontWeight: '500',
                    fontSize: '14px'
                  }}
                >
                  Potência (W): <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  type="number"
                  id="novo-modulo-potencia"
                  value={novoModuloForm.potencia_wp}
                  onChange={(e) => setNovoModuloForm({ ...novoModuloForm, potencia_wp: e.target.value })}
                  placeholder="Ex: 550"
                  min="1"
                  required
                  disabled={salvandoModulo}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    transition: 'border-color 0.3s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#667eea'}
                  onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                />
              </div>

              <div style={{ 
                display: 'flex', 
                gap: '10px', 
                justifyContent: 'flex-end',
                paddingTop: '15px',
                borderTop: '1px solid #e0e0e0'
              }}>
                <button
                  type="button"
                  onClick={() => !salvandoModulo && setShowNovoModuloModal(false)}
                  disabled={salvandoModulo}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: salvandoModulo ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'background-color 0.3s',
                    opacity: salvandoModulo ? 0.6 : 1
                  }}
                  onMouseOver={(e) => !salvandoModulo && (e.target.style.backgroundColor = '#5a6268')}
                  onMouseOut={(e) => e.target.style.backgroundColor = '#6c757d'}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={salvandoModulo}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: salvandoModulo ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'background-color 0.3s',
                    opacity: salvandoModulo ? 0.6 : 1
                  }}
                  onMouseOver={(e) => !salvandoModulo && (e.target.style.backgroundColor = '#218838')}
                  onMouseOut={(e) => e.target.style.backgroundColor = '#28a745'}
                >
                  {salvandoModulo ? '⏳ Salvando...' : '💾 Salvar Módulo'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default CalculadoraSolar
